import { createContext, useContext, type MutableRefObject } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import type { MarkerSelectionData } from 'tripPlanner/components/Chat/chatMapConstants'
import type { RightPanelView } from '../hooks/useRightPanelState'
import type { DrawerStackEntry } from '../hooks/useDrawerStackState'

export interface TurnAuthoritySnapshot {
  rightPanelView: RightPanelView | undefined
  selectedDayItineraryId: number | undefined
  selectedMarkerData: MarkerSelectionData | undefined
  isAnyDrawerOpen: boolean
  topEntry: DrawerStackEntry | undefined
}

export const DEFAULT_TURN_AUTHORITY_SNAPSHOT: TurnAuthoritySnapshot = {
  rightPanelView: undefined,
  selectedDayItineraryId: undefined,
  selectedMarkerData: undefined,
  isAnyDrawerOpen: false,
  topEntry: undefined,
}

export const TurnAuthoritySnapshotContext = createContext<MutableRefObject<TurnAuthoritySnapshot> | undefined>(undefined)

/**
 * Returns a stable ref (not state) holding the send-time right-panel/drawer snapshot -- read
 * imperatively via `.current`, never during render. A future `resolveTurnAuthority` callback
 * (Phase 1) reads this at call time to populate `visibleState`; this PR only wires it through.
 */
export function useTurnAuthoritySnapshot(): MutableRefObject<TurnAuthoritySnapshot> {
  const context = useContext(TurnAuthoritySnapshotContext)
  if (!context) {
    throw new Error('useTurnAuthoritySnapshot must be used within TripChatRuntime')
  }

  return context
}

interface RightPanelSnapshotRegistrationProps {
  rightPanelView: RightPanelView
  selectedDayItineraryId: number | undefined
  selectedMarkerData: MarkerSelectionData | undefined
}

export function RightPanelSnapshotRegistration({
  rightPanelView,
  selectedDayItineraryId,
  selectedMarkerData,
}: RightPanelSnapshotRegistrationProps) {
  const snapshotRef = useTurnAuthoritySnapshot()

  useIsomorphicLayoutEffect(() => {
    snapshotRef.current.rightPanelView = rightPanelView
    snapshotRef.current.selectedDayItineraryId = selectedDayItineraryId
    snapshotRef.current.selectedMarkerData = selectedMarkerData
  }, [snapshotRef, rightPanelView, selectedDayItineraryId, selectedMarkerData])

  // Separate effect, empty deps -- runs its cleanup only on true unmount, not on every value
  // change. RightPanelContainer has no mobile counterpart (desktop-only mount point), and the
  // desktop/mobile surface can flip at runtime (viewport resize/rotation), not just on initial
  // load -- without this, these three fields would freeze at their last desktop values
  // indefinitely once the surface switches to mobile.
  useIsomorphicLayoutEffect(() => () => {
    snapshotRef.current.rightPanelView = DEFAULT_TURN_AUTHORITY_SNAPSHOT.rightPanelView
    snapshotRef.current.selectedDayItineraryId = DEFAULT_TURN_AUTHORITY_SNAPSHOT.selectedDayItineraryId
    snapshotRef.current.selectedMarkerData = DEFAULT_TURN_AUTHORITY_SNAPSHOT.selectedMarkerData
  }, [snapshotRef])

  return null
}

interface DrawerSnapshotRegistrationProps {
  isAnyDrawerOpen: boolean
  topEntry: DrawerStackEntry | undefined
}

export function DrawerSnapshotRegistration({ isAnyDrawerOpen, topEntry }: DrawerSnapshotRegistrationProps) {
  const snapshotRef = useTurnAuthoritySnapshot()

  useIsomorphicLayoutEffect(() => {
    snapshotRef.current.isAnyDrawerOpen = isAnyDrawerOpen
    snapshotRef.current.topEntry = topEntry
  }, [snapshotRef, isAnyDrawerOpen, topEntry])

  // Lower risk than RightPanelSnapshotRegistration's unmount case -- DrawerContainer's desktop
  // and mobile mount points are mutually exclusive but always have exactly one active instance
  // whenever any Trip Chat surface is rendered, so a breakpoint flip always has a replacement
  // writer in the same commit. Reset on unmount anyway for consistency/defense-in-depth.
  useIsomorphicLayoutEffect(() => () => {
    snapshotRef.current.isAnyDrawerOpen = DEFAULT_TURN_AUTHORITY_SNAPSHOT.isAnyDrawerOpen
    snapshotRef.current.topEntry = DEFAULT_TURN_AUTHORITY_SNAPSHOT.topEntry
  }, [snapshotRef])

  return null
}
